import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { CustomIcon } from "components/icon/customIcon.component";
import { Radius, Spacing } from "components/designV2/constants";

export type ToastVariant = "error" | "success" | "info";

export const DEFAULT_TOAST_DURATION_MS = 5000;

interface ToastProps {
  readonly variant: ToastVariant;
  readonly message: string;
  readonly title?: string;
  readonly durationMs?: number | null;
  readonly onDismiss: () => void;
}

const VARIANT_STYLES: Record<
  ToastVariant,
  { accentColor: ColorV2; backgroundColor: ColorV2; iconName: string }
> = {
  error: {
    accentColor: ColorV2.NegativeRed,
    backgroundColor: ColorV2.NegativeRedLight,
    iconName: "v2/warning-triangle",
  },
  success: {
    accentColor: ColorV2.PositiveGreen,
    backgroundColor: ColorV2.PositiveGreenLight,
    iconName: "v2/checkmark",
  },
  info: {
    accentColor: ColorV2.ElectricBlue,
    backgroundColor: ColorV2.Blue7,
    iconName: "v2/info",
  },
};

/** A single toast message with an icon, text, and a dismiss button. */
export const Toast = ({
  variant,
  message,
  title,
  durationMs = DEFAULT_TOAST_DURATION_MS,
  onDismiss,
}: ToastProps): React.ReactElement => {
  const { t } = useTranslation();
  const { accentColor, backgroundColor, iconName } = VARIANT_STYLES[variant];

  // Kept in a ref so a caller re-creating its callback on every render cannot
  // restart the countdown and leave the toast on screen.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (durationMs == null) {
      return;
    }

    const timeoutId = setTimeout(() => onDismissRef.current(), durationMs);

    return () => clearTimeout(timeoutId);
  }, [durationMs]);

  return (
    <Container
      role={variant === "error" ? "alert" : "status"}
      $accentColor={accentColor}
      $backgroundColor={backgroundColor}
    >
      <CustomIcon name={iconName} size="24px" color={accentColor} />
      <Content>
        {title && <Title>{title}</Title>}
        <Message>{message}</Message>
      </Content>
      <DismissButton
        type="button"
        aria-label={t("DesignComponents.Close")}
        onClick={onDismiss}
      >
        <CustomIcon name="v2/cross" size="20px" color={ColorV2.Blue4} />
      </DismissButton>
      {durationMs != null && (
        <ProgressTrack aria-hidden="true">
          <ProgressBar $accentColor={accentColor} $durationMs={durationMs} />
        </ProgressTrack>
      )}
    </Container>
  );
};

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shrink = keyframes`
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
`;

const Container = styled.div<{
  $accentColor: ColorV2;
  $backgroundColor: ColorV2;
}>`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: ${Spacing.sm}px;
  width: 100%;
  padding: ${Spacing.md}px;
  border: 1.5px solid ${(props) => props.$accentColor};
  border-radius: ${Radius.xl}px;
  overflow: hidden;
  background-color: ${(props) => props.$backgroundColor};
  box-shadow: 0 6px 16px rgba(15, 42, 64, 0.12);
  animation: ${slideIn} 0.2s ease;
  pointer-events: auto;
`;

const Content = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${Spacing.xs}px;
  min-width: 0;
`;

const Title = styled.span`
  ${TypographyV2.b1}
  color: ${ColorV2.Blue1};
`;

const Message = styled.span`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue2};
  overflow-wrap: anywhere;
`;

const ProgressTrack = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 3px;
`;

const ProgressBar = styled.div<{
  $accentColor: ColorV2;
  $durationMs: number;
}>`
  height: 100%;
  background-color: ${(props) => props.$accentColor};
  transform-origin: left;
  animation: ${shrink} ${(props) => props.$durationMs}ms linear forwards;
`;

const DismissButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${ColorV2.Blue3};
    outline-offset: 2px;
    border-radius: ${Radius.sm}px;
  }
`;
